import {
    BxfIcon,
    BxfIconType,
    BxfTargetAtrEnum,
    BxfTooltip,
} from '@blk/bxf-react-components';
import styled from '@emotion/styled';

import { useMemo, useRef, useState } from 'react';
import {
    ChatExchangeConstants,
    ContactCardConstants,
    COPY_TOOLTIP_LABEL,
    COPY_TOOLTIP_SUCCESS_LABEL,
    DEFAULT_BUSINESS_HOURS,
    DEFAULT_EMAIL_CONTACT,
    DEFAULT_PHONE_CONTACT,
    DEFAULT_SCHEDULE_URI,
    SCHEDULE_MEETING_LABEL,
} from '../constants';
import { useChatBlkStore } from '../hooks';
import { ChatBlkTelemetryService } from '../services';
import { ChatBlkRelationshipManager, telemetryFunctionMap } from '../types';
import { getKey } from '../utils';

const Avatar = styled.div<{ width?: number }>`
    width: 100%;
    height: 88px;
    display: flex;
    margin: auto;
    align-items:  ${(props) => (props.width > 500 ? 'center' : 'start !important')};
    align-items: center;
    flex-direction: column;
    background-size: cover;
    justify-content:  ${(props) => (props.width > 500 ? 'center' : 'start')};
    background-position: center;
    border-radius: 56px;
}
`;

const ContactInfo = styled.div`
    display: flex;
    flex-direction: column;
    margin-left: 10px;
    height: 100%;
    white-space: nowrap;
`;

const Name = styled.span<{ width?: number }>`
    color: #000;
    text-align: ${(props) => (props.width > 500 ? 'center' : 'start')};
    font-feature-settings:
        'liga' off,
        'clig' off;

    /* Responsive/Body S Compact Bold */
    font-family: var(--Typography-Family-Body, 'BLK Fort');
    font-size: var(--Typography-Size-Body-S, 14px);
    font-style: normal;
    font-weight: 700;
    line-height: var(
        --Typography-Line-height-Body-S-Compact,
        18px
    ); /* 128.571% */
    letter-spacing: var(--Typography-Letter-spacing-Body-S-Compact, 0px);
`;

const Email = styled.span`
    font-weight: 400;
    padding: 8px 0;
    color: var(--Color-Text-text-default, #000);
    white-space: nowrap;
    text-decoration: underline;
    display: flex !important;
    font-feature-settings:
        'liga' off,
        'clig' off;

    /* Responsive/Body S Compact Link */
    font-family: var(--Typography-Family-Body, 'BLK Fort');
    font-size: var(--Typography-Size-Body-S-Compact, 14px);
    font-style: normal;
    font-weight: 700;
    line-height: var(
        --Typography-Line-height-Body-S-Compact,
        18px
    ); /* 128.571% */
    letter-spacing: var(--Typography-Letter-spacing-Body-S-Compact, 0px);
    text-decoration-line: underline;
    text-decoration-style: solid;
    text-decoration-skip-ink: auto;
    text-decoration-thickness: 1px;
    text-underline-offset: 2px;
    text-underline-position: from-font;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: nowrap;
    flex-direction: row;
    justify-content: center;
    cursor: pointer;

    &:hover {
        color: #ff4713;
        cursor: pointer !important;
    }
`;

const Phone = styled.span`
    color: #000;
    white-space: nowrap;
    text-align: center;
    display: flex !important;
    font-feature-settings:
        'liga' off,
        'clig' off;
    font-family: var(--Typography-Family-Body, 'BLK Fort');
    font-size: var(--Typography-Size-Body-S, 14px);
    font-style: normal;
    font-weight: 700;
    line-height: var(
        --Typography-Line-height-Body-S-Compact,
        18px
    ); /* 128.571% */
    letter-spacing: var(--Typography-Letter-spacing-Body-S-Compact, 0px);
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: nowrap;
    flex-direction: row;
    justify-content: start;
    cursor: pointer;

    &:hover {
        color: #ff4713;
        fill: #ff4713;
        cursor: pointer !important;
    }
`;

const BusinessHours = styled.span`
    color: #000;
    white-space: nowrap;
    display: flex !important;
    text-align: left;
    justify-content: start;
    font-feature-settings:
        'liga' off,
        'clig' off;
    font-family: var(--Typography-Family-Body, 'BLK Fort');
    font-size: var(--Typography-Size-Body-S, 14px);
    font-style: normal;
    font-weight: 700;
    line-height: var(
        --Typography-Line-height-Body-S-Compact,
        18px
    ); /* 128.571% */
    letter-spacing: var(--Typography-Letter-spacing-Body-S-Compact, 0px);
    padding: 8px 0;
    display: flex;
    gap: 0.5rem;
    flex-wrap: nowrap;
    flex-direction: row;
`;

const ContactProfile = styled.div<{ width?: number }>`
    color: #000;
    text-align: ${(props) => (props.width > 500 ? 'center' : 'start')};
    font-feature-settings:
        'liga' off,
        'clig' off;
    font-family: var(--Typography-Family-Body, 'BLK Fort');
    font-size: var(--Typography-Size-Body-S, 14px);
    font-style: normal;
    font-weight: 700;
    line-height: var(
        --Typography-Line-height-Body-S-Compact,
        18px
    ); /* 128.571% */
    justify-content: ${(props) => (props.width > 500 ? 'center' : 'start')};
    justify-items: ${(props) => (props.width > 500 ? 'center' : 'start')};
    display: grid;
    align-items: ${(props) => (props.width > 500 ? 'center' : 'start')};
    gap: 8px;
    letter-spacing: var(--Typography-Letter-spacing-Body-S-Compact, 0px);
`;

const UserRole = styled.div<{ width?: number }>`
    color: #000;
    text-align: ${(props) => (props.width > 500 ? 'center' : 'start')};
    font-feature-settings:
        'liga' off,
        'clig' off;

    /* Responsive/Body S Compact */
    font-family: var(--Typography-Family-Body, 'BLK Fort');
    font-size: var(--Typography-Size-Body-S-Compact, 14px);
    font-style: normal;
    font-weight: 400;
    line-height: var(
        --Typography-Line-height-Body-S-Compact,
        18px
    ); /* 128.571% */
    letter-spacing: var(--Typography-Letter-spacing-Body-S-Compact, 0px);
`;

const Tooltip = styled(BxfTooltip)`
    padding: 8px 0;
    cursor: pointer;
    width: min-content;
`;

const ContactCardContainer = styled.div<{ width?: number }>`
    display: grid;
    grid-template-columns: ${(props) =>
        props.width > 400 ? '1fr 2fr' : '1fr'};
    align-items: ${(props) => (props.width > 500 ? 'center' : 'start')};
    padding: 2rem;
    padding-left: 0;
    gap: 2rem;
    flex-wrap: wrap;
`;

const ScheduleMeetingLink = styled.a`
    display: flex;
    height: 32px;
    padding: 8px 16px;
    justify-content: center;
    align-items: center;
    gap: 4px;
    border-radius: 24px;
    border: 1px solid var(--Color-Background-bg-inverse, #000);
    color: var(--Color-Background-bg-inverse, #000);
    text-decoration: none;
    width: min-content;
    padding: 0.5rem 1rem;
    font-weight: 700;
    height: min-content;
    margin: 0.5rem 0;

    &:hover {
        color: #ff4713;
        fill: #ff4713;
        border-color: #ff4713;
    }
`;

const InitialsDefaultContact = styled.div`
    background-position: center;
    background-size: cover;
    color: var(--Core-blk-core-black, #000);
    background-color: var(--Core-blk-core-white, #fff);
    border-radius: 56px;
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    font-feature-settings:
        'liga' off,
        'clig' off;

    font-family: var(--Typography-Family-one-ds-heading, 'BLK Fort');
    font-size: var(--Typography-Size-one-ds-heading-xxl, 33px);
    font-style: normal;
    font-weight: 800;
    line-height: var(
        --Typography-Line-height-one-ds-heading-xxl,
        40px
    ); /* 121.212% */
    width: 88px;
    height: 88px;
    flex-shrink: 0;
    align-content: center;
    letter-spacing: var(--Typography-Letter-spacing-one-ds-heading-xxl, 0px);
`;

const { TARGET_SLOT, CONTENT_SLOT } = ChatExchangeConstants;

const TooltippedInfo = ({ value, icon }: { value: string; icon: string }) => {
    const [isHover, setIsHover] = useState(false);
    const tooltipRef = useRef<HTMLBxfTooltipElement>(null);
    const [tooltipContent, setTooltipContent] = useState(COPY_TOOLTIP_LABEL);

    const handleContactMouseEnter = () => {
        setTooltipContent(COPY_TOOLTIP_LABEL);
        setIsHover(true);
    };
    const handleContactMouseLeave = () => {
        setIsHover(false);
        tooltipRef.current?.hide();
        setTooltipContent(COPY_TOOLTIP_LABEL);
    };
    const handleContactTooltipClosed = () => {
        tooltipRef.current?.show();
        setTooltipContent(COPY_TOOLTIP_SUCCESS_LABEL);
    };
    const handleCopyContact = (e) => {
        navigator.clipboard.writeText(value);
        e.stopPropagation();
        e.preventDefault();

        setTooltipContent(COPY_TOOLTIP_SUCCESS_LABEL);
        tooltipRef.current?.show();

        ChatBlkTelemetryService.track(
            telemetryFunctionMap.CHATBLK_MFE_FUNCTION_RM_EMAIL_COPIED,
            { [`${value}`]: value }
        );
    };
    const Component = icon === 'bxf-email' ? Email : Phone;
    const dataLinkEvent =
        icon === 'bxf-email'
            ? 'chatblk | menu | contact rm email | copy'
            : 'chatblk | menu | contact rm number | copy';

    return (
        <Tooltip
            ref={tooltipRef}
            key={getKey(value, 'tooltip')}
            onTooltipClosed={handleContactTooltipClosed}
        >
            <Component
                slot={TARGET_SLOT}
                data-link-event={dataLinkEvent}
                onMouseEnter={handleContactMouseEnter}
                onMouseLeave={handleContactMouseLeave}
                onClick={handleCopyContact}
            >
                <BxfIcon
                    type={icon as BxfIconType}
                    state={isHover ? 'primary-hover' : 'primary'}
                />
                {value}
            </Component>
            <div slot={CONTENT_SLOT}>{tooltipContent}</div>
        </Tooltip>
    );
};

const ContactPhoto = ({
    name,
    role,
    panelWidth,
}: {
    name: string;
    role: string;
    panelWidth: number;
}) =>
    name ? (
        <ContactProfile width={panelWidth}>
            <Avatar width={panelWidth}>
                <InitialsDefaultContact>
                    {name
                        .split(' ')
                        .map((word) => word.charAt(0).toUpperCase())
                        .join('') || ''}
                </InitialsDefaultContact>
            </Avatar>
            <Name width={panelWidth}>{name}</Name>
            <UserRole width={panelWidth}>{role}</UserRole>
        </ContactProfile>
    ) : null;

/**
 * A functional component that renders a contact card with user details such as name, email, phone, avatar, and role.
 * It also provides functionality to copy email and phone to the clipboard and displays tooltips on hover.
 *
 * @param {ChatBlkRelationshipManager} props - The properties for the contact card.
 * @returns {JSX.Element} A JSX element representing the contact card.
 */
const RelationshipManagerContacts = (): JSX.Element => {
    const { relationshipManagers, panelWidth } = useChatBlkStore();
    const shouldShowDefaultContact = useMemo(() => {
        return (
            !relationshipManagers ||
            relationshipManagers?.some(
                (manager) => !manager?.role || manager?.role === 'DEFAULT'
            )
        );
    }, [relationshipManagers]);

    return (
        <>
            {(relationshipManagers?.length ? relationshipManagers : [{}]).map(
                ({
                    email = DEFAULT_EMAIL_CONTACT,
                    phone = DEFAULT_PHONE_CONTACT,
                    availability = DEFAULT_BUSINESS_HOURS,
                    scheduleMeetingUri = shouldShowDefaultContact
                        ? DEFAULT_SCHEDULE_URI
                        : undefined,
                    name = '',
                    role = '',
                }: ChatBlkRelationshipManager) => {
                    const handleScheduleMeeting = () => {
                        ChatBlkTelemetryService.track(
                            telemetryFunctionMap.CHATBLK_MFE_FUNCTION_RM_SCHEDULE_MEETING_CLICKED,
                            null
                        );
                    };

                    return (
                        <ContactCardContainer
                            key={name || role || email || phone}
                            data-cy={ContactCardConstants.DATA_CY_CONTACT_CARD}
                            width={panelWidth}
                        >
                            <ContactPhoto
                                name={name}
                                role={role}
                                panelWidth={panelWidth}
                            />
                            <ContactInfo>
                                <TooltippedInfo
                                    value={phone}
                                    icon={'bxf-phone'}
                                    key={'bxf-phone'}
                                />
                                <TooltippedInfo
                                    value={email}
                                    icon={'bxf-email'}
                                    key={'bxf-email'}
                                />
                                {shouldShowDefaultContact && (
                                    <BusinessHours slot={TARGET_SLOT}>
                                        <BxfIcon
                                            type='bxf-calendar'
                                            onClick={handleScheduleMeeting}
                                        />
                                        {availability}
                                    </BusinessHours>
                                )}
                                {scheduleMeetingUri && (
                                    <ScheduleMeetingLink
                                        href={scheduleMeetingUri}
                                        target={BxfTargetAtrEnum.BLANK}
                                        data-link-event='chatblk | menu | contact rm | link click | schedule a meeting'
                                    >
                                        {SCHEDULE_MEETING_LABEL}
                                    </ScheduleMeetingLink>
                                )}
                            </ContactInfo>
                        </ContactCardContainer>
                    );
                }
            )}
        </>
    );
};

export default RelationshipManagerContacts;
